import { useState, useEffect } from 'react';
import { lexBotApi, LexBotConfig } from '../lib/lexBotApi';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Alert } from './ui/alert';
import { Switch } from './ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { BotUtteranceEditor } from './BotUtteranceEditor';

interface BotManagementProps {
  tenantId: string;
  planType: string;
}

export function BotManagement({ tenantId, planType }: BotManagementProps) {
  const [botConfig, setBotConfig] = useState<LexBotConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');

  const isEnterprise = planType === 'enterprise';

  useEffect(() => {
    if (isEnterprise) {
      loadBotConfig();
    }
  }, [tenantId, isEnterprise]);

  // Poll for in-progress bot creation
  useEffect(() => {
    if (botConfig && (botConfig.status === 'CREATING' || botConfig.status === 'BUILDING')) {
      const interval = setInterval(() => loadBotConfig(), 5000);
      return () => clearInterval(interval);
    }
  }, [botConfig?.status]);

  const loadBotConfig = async () => {
    try {
      const config = await lexBotApi.getBot(tenantId);
      setBotConfig(config);
    } catch (err: any) {
      if (err.message?.includes('404')) {
        setBotConfig(null);
      }
    }
  };

  const handleCreate = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await lexBotApi.createBot(tenantId);
      console.log('Bot created successfully:', result);
      await loadBotConfig();
    } catch (err: any) {
      console.error('Bot creation error:', err);
      // Handle timeout gracefully - bot may still be creating
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout') || err.message?.includes('504')) {
        setError('Bot creation in progress. Refresh in 30 seconds to check status.');
        // Poll for completion
        setTimeout(() => loadBotConfig(), 30000);
      } else {
        const errorMsg = err.response?.data?.error || err.message || 'Failed to create bot';
        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLocale = async () => {
    setLoading(true);
    setError(null);
    try {
      await lexBotApi.createLocale(tenantId);
      await loadBotConfig();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create locale');
    } finally {
      setLoading(false);
    }
  };



  const handleToggleBot = async (enabled: boolean) => {
    setLoading(true);
    setError(null);
    try {
      if (enabled) {
        await lexBotApi.enableBot(tenantId);
      } else {
        await lexBotApi.disableBot(tenantId);
      }
      await loadBotConfig();
    } catch (err: any) {
      setError(err.response?.data?.error || `Failed to ${enabled ? 'enable' : 'disable'} bot`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Delete custom bot? This cannot be undone.')) return;
    setLoading(true);
    setError(null);
    try {
      await lexBotApi.deleteBot(tenantId);
      setBotConfig(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete bot');
    } finally {
      setLoading(false);
    }
  };



  if (!isEnterprise) {
    return (
      <Alert>
        <p className="font-semibold">Enterprise Feature</p>
        <p className="text-sm">Custom Lex bots are available on Enterprise tier.</p>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <p>{error}</p>
        </Alert>
      )}

      {!botConfig ? (
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-2">Custom Lex Bot</h3>
          <p className="text-sm text-gray-600 mb-4">
            Create a dedicated Amazon Lex bot for your tenant with custom intents and sample utterances.
          </p>
          <Button onClick={handleCreate} disabled={loading}>
            {loading ? 'Creating bot...' : 'Create Custom Bot'}
          </Button>
          {loading && (
            <div className="mt-4">
              <div className="animate-pulse flex space-x-2 mb-2">
                <div className="h-2 bg-blue-400 rounded w-1/3"></div>
              </div>
              <p className="text-xs text-gray-600">Creating bot structure...</p>
            </div>
          )}
        </Card>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="utterances">Sample Utterances</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold">Custom Bot Status</h3>
                  <p className="text-sm text-gray-600">Use your custom bot instead of the shared bot</p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant={botConfig.status === 'ENABLED' ? 'default' : 'secondary'}>
                    {botConfig.status}
                  </Badge>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">
                      {botConfig.custom_lex_enabled ? 'Enabled' : 'Disabled'}
                    </span>
                    <Switch
                      checked={botConfig.custom_lex_enabled || false}
                      onCheckedChange={handleToggleBot}
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div><span className="font-medium">Bot ID:</span> {botConfig.botId}</div>
                <div><span className="font-medium">Version:</span> {botConfig.botVersion}</div>
                <div><span className="font-medium">Alias:</span> {botConfig.botAliasId}</div>
                <div><span className="font-medium">Locale:</span> {botConfig.botLocaleId}</div>
                <div><span className="font-medium">Updated:</span> {new Date(botConfig.lastUpdatedAt).toLocaleString()}</div>
              </div>
            </Card>

            {botConfig.status !== 'READY' && (
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Bot Setup Wizard</h3>
                <div className="space-y-4">
                  {botConfig.status === 'CREATING' && (
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600">Step 1: Create the bot locale (language configuration)</p>
                      <Button onClick={handleCreateLocale} disabled={loading} variant="default">
                        {loading ? 'Creating...' : '1. Create Locale'}
                      </Button>
                      <p className="text-xs text-gray-500">Creates the English (US) locale. Returns immediately.</p>
                    </div>
                  )}
                  {botConfig.status === 'ENABLED' && (
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600">Step 2: Copy intents and build locale</p>
                      <Button onClick={async () => {
                        setLoading(true);
                        setError(null);
                        try {
                          await lexBotApi.copyIntents(tenantId);
                          await loadBotConfig();
                        } catch (err: any) {
                          setError(err.response?.data?.error || 'Failed to copy intents');
                        } finally {
                          setLoading(false);
                        }
                      }} disabled={loading} variant="default">
                        {loading ? 'Copying & Building...' : '2. Copy Intents & Build'}
                      </Button>
                      <p className="text-xs text-gray-500">Copies intents from default bot (TCHLAQX9JH) and builds locale (~60s).</p>
                    </div>
                  )}
                  {botConfig.status === 'BUILDING' && (
                    <div className="space-y-2">
                      <div className="animate-pulse flex space-x-2 mb-2">
                        <div className="h-2 bg-blue-400 rounded w-1/3"></div>
                        <div className="h-2 bg-blue-400 rounded w-1/3"></div>
                        <div className="h-2 bg-blue-400 rounded w-1/3"></div>
                      </div>
                      <p className="text-sm text-gray-600">Locale is building... (~60 seconds)</p>
                      <Button onClick={loadBotConfig} disabled={loading} variant="outline" size="sm" className="mt-2">
                        {loading ? 'Checking...' : 'Refresh Status'}
                      </Button>
                      <p className="text-xs text-gray-500">Auto-refreshes every 5 seconds, or click to check now.</p>
                    </div>
                  )}
                </div>
              </Card>
            )}

            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Danger Zone</h3>
              <div className="flex gap-2">
                <Button onClick={handleDelete} disabled={loading} variant="destructive">
                  Delete Custom Bot
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-2">Deleting the bot will revert to using the shared bot.</p>
            </Card>
          </TabsContent>

          <TabsContent value="utterances">
            <BotUtteranceEditor tenantId={tenantId} botId={botConfig.botId} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
